import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { api } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { RefreshCw, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const FrequentPostingsPage = () => {
  const [postings, setPostings] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', debit_account: '', credit_account: '', amount: '', description: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([api.get('/frequent-postings'), api.get('/chart-of-accounts')]);
      setPostings(pRes.data || []);
      setAccounts((aRes.data || []).sort((a, b) => (a.account_code || '').localeCompare(b.account_code || '')));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name || !form.debit_account || !form.credit_account || !form.amount) { toast.error('All fields required'); return; }
    try {
      await api.post('/frequent-postings', form);
      toast.success('Frequent posting template created');
      setShowModal(false);
      setForm({ name: '', debit_account: '', credit_account: '', amount: '', description: '' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handlePost = async (posting) => {
    try {
      await api.post('/journal-entries', {
        description: posting.description || `Frequent Posting: ${posting.name}`,
        entries: [
          { account_code: posting.debit_account, debit: posting.amount, credit: 0 },
          { account_code: posting.credit_account, debit: 0, credit: posting.amount },
        ],
      });
      toast.success('Journal entry posted — pending approval');
    } catch (e) { toast.error('Failed to post'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/frequent-postings/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const getAccName = (code) => { const a = accounts.find(x => x.account_code === code); return a ? `${code} - ${a.account_name}` : code; };

  return (
    <AdminSubPage title="Frequent Postings" breadcrumb={['Accounting', 'Frequent Postings']} backPath="/dashboard/accounting">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Quick-post recurring journal entries with predefined templates.</p>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] text-white text-sm gap-1.5" data-testid="add-frequent-posting">
          <Plus className="w-4 h-4" /> Add Template
        </Button>
      </div>
      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Template Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Debit</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Credit</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Amount</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            postings.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">No templates yet. Click "Add Template" to create one.</td></tr> :
            postings.map(p => (
              <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`fp-row-${p.id}`}>
                <td className="px-4 py-3"><p className="font-medium text-sm text-[#1f1f1f]">{p.name}</p>{p.description && <p className="text-[10px] text-gray-400">{p.description}</p>}</td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{getAccName(p.debit_account)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{getAccName(p.credit_account)}</td>
                <td className="px-4 py-3 text-sm font-medium text-right">{formatCurrency(p.amount)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handlePost(p)} className="text-xs h-7" data-testid={`fp-post-${p.id}`}><RefreshCw className="w-3 h-3 mr-1" />Post</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-xs h-7 text-red-500"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="fp-desc">
          <DialogHeader><DialogTitle>Add Frequent Posting Template</DialogTitle></DialogHeader>
          <p id="fp-desc" className="sr-only">Create a reusable posting template</p>
          <div className="space-y-4">
            <div><Label>Template Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Monthly Salaries" data-testid="fp-name" /></div>
            <div><Label>Debit Account *</Label>
              <Select value={form.debit_account} onValueChange={v => setForm({...form, debit_account: v})}>
                <SelectTrigger data-testid="fp-debit"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.account_code} value={a.account_code}><span className="font-mono mr-2">{a.account_code}</span>{a.account_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Credit Account *</Label>
              <Select value={form.credit_account} onValueChange={v => setForm({...form, credit_account: v})}>
                <SelectTrigger data-testid="fp-credit"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.account_code} value={a.account_code}><span className="font-mono mr-2">{a.account_code}</span>{a.account_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount (ZAR) *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="fp-amount" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#1f1f1f] text-white" data-testid="fp-submit">Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default FrequentPostingsPage;
